(ns assemble.component.upload-modal
  "Shared parts for asset, font and template upload modals.

  These functions don't use re-frame directly, instead the state
  and event handlers are passed in from the views."
  (:require ["@mui/material/Breadcrumbs$default" :as Breadcrumbs]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Dialog$default" :as Dialog]
            ["@mui/material/DialogActions$default" :as DialogActions]
            ["@mui/material/DialogTitle$default" :as DialogTitle]
            ["@mui/material/Grid$default" :as Grid]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/LinearProgress$default" :as LinearProgress]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium GridActionsCellItem
                                                GridCell GridRow useGridApiContext]]
            ["react" :as react]
            [assemble.component.dropzone :refer [dropzone-container
                                                 upload-area-placeholder]]
            [assemble.util :as util]
            [clojure.string :as str]
            [linked.core :as l]
            [uix.core :refer [$ defui]]))

(defn queue-first-by-status [queue status]
  (some (fn [[_id item]]
          (when (= status (:status item))
            item))
        queue))

(defn files->queue [files validate-fn extend-fn]
  ;; We could also check the filesize is supported, if we have
  ;; a definite limit. Right now S3 supports 5GB upload, as we
  ;; aren't uploading in multiple parts.
  (into (l/map)
        (map (fn [file]
               (let [id (str (random-uuid))
                     filename (.-name file)
                     value {:id id
                            :status nil
                            :filename filename
                            :type (.-type file)
                            :size (.-size file)
                            :file file
                            :progress nil
                            :metadata nil}
                     valid-type (validate-fn value)
                     value (assoc value :status (if valid-type
                                                  "queue"
                                                  "unsupported"))]
                 [id (extend-fn value)]))
             files)))

(defn merge-queue [a b]
  ((fnil into (l/map)) a b))

(defn remove-queue-item [queue id]
  ;; UI shouldn't allow removing uploading files, but check to be safe.
  ;; Removing in-progress upload would break app-db, because
  ;; progress event handler will update this state.
  (if (= "uploading" (get-in queue [id :status]))
    queue
    (dissoc queue id)))

(defn render-cell-upload-status
  "This is React function component - not safe to use RAtoms here."
  ([^js o]
   (render-cell-upload-status o nil))
  ([^js o {:keys [error-messages]}]
   (let [metadata (:metadata (.-row o))
         status (.-value o)
         validation (when metadata (-> metadata
                                       :validation
                                       first
                                       :message))
         error-message-long (get error-messages (:error-code (:error (.-row o))) "")
         error-message (str "Upload failed" (when (and (string? error-message-long)
                                                       (not (str/blank? error-message-long)))
                                              (str ": " (subs error-message-long 0 5) "...")))

         [icon icon-color message message-longer link]
         (case status
           "done"        ["check_circle" "success" "Upload successful" ""]
           "uploading"   ["check_circle" "primary" "Upload in progress" ""]
           "error"       ["error"        "error"   error-message error-message-long]
           "unsupported" ["error"        "error"   "Unsupported file type" ""]
           "invalid"     (apply conj ["error" "error"] validation)
           ;; Default to queue state, e.g. read-zip, reading-zip.
           ;; "queue"
           ["info"        "info"         "Upload queued" ""])

         progress (some-> (:progress (.-row o))
                          (js/Math.round))]
     (if (and progress (= "uploading" status))
       ($ :<>
          ($ Typography
             {:sx #js {:mr 1}}
             progress "%")
          ($ LinearProgress
             {:sx #js {:flex 1}
              :variant "determinate"
              :value progress
              :color icon-color}))
       ($ Tooltip
          {:placement "bottom"
           :title (when-not (str/blank? message-longer)
                    ($ :<>
                       ($ :span message-longer)
                       ($ :a {:href link
                              :target "_blank"} link)))}
          ($ Stack
             {:direction "row"
              :gap 1
              :alignItems "center"
              :sx #js {:overflow "hidden"}}
             (when icon
               ($ Icon
                  {:color icon-color}
                  icon))
             ($ Typography
                {:sx #js {:py 1}
                 :noWrap true}
                message)))))))

(def icon-column
  #js {:field "icon"
       :headerName ""
       :valueGetter (fn [_value row ^js _column ^js _apiRef] (:filename row))
       :align "center"
       :sortable false
       :renderCell (fn [^js o]
                     (let [{:keys [icon]} (util/get-file-type-parameters (.-value o))]
                       ($ Icon
                          ;; For some reason :color property doesn't work
                          ;; directly, but sx works.
                          {:sx #js {:color "text.secondary"}}
                          (or icon "insert_drive_file"))))
       :width 16})

(def name-column
  #js {:field "name"
       :headerName "Name"
       :valueGetter (fn [_value row ^js _column ^js _apiRef] (:filename row))
       :sortable false
       :minWidth 74
       :flex 2
       :renderCell (fn [^js o]
                     ;; As the table is using auto rowHeight,
                     ;; we need something to set basic padding for single-line content.
                     ;; This padding matches the default pretty closely.
                     ($ Typography
                        {:noWrap true
                         :sx #js {:py 2
                                  :overflow "hidden"}}
                        (.-value o)))})

(def type-column
  #js {:field "type"
       :headerName "Type"
       :valueGetter (fn [_value row ^js _column ^js _apiRef] (util/file-extension (:filename row)))
       :sortable false
       :width 80})

(def size-column
  #js {:field "size"
       :headerName "Size"
       :valueGetter (fn [_value row ^js _column ^js _apiRef] (:size row))
       :valueFormatter (fn [value _row ^js _column ^js _apiRef]
                         (util/format-file-size value))
       :sortable false
       :width 80})

(def upload-status-column
  #js {:field "status"
       :headerName ""
       :valueGetter (fn [_value row ^js _column ^js _apiRef] (:status row))
       :renderCell render-cell-upload-status
       :sortable false
       :minWidth 64
       :flex 3})

(defn create-action-column
  [{:keys [on-retry on-delete]}]
  #js {:field "action"
       :type "actions"
       :width 16
       :getActions (fn [^js o]
                     (let [status  (:status (.-row o))]
                       ;; Cancelling in-progress uploads isn't supported now, hide the button.
                       ;; Once upload is done, "x" button would be confusing, as it doesn't remove the asset,
                       ;; just removes it from the queue.
                       (if (#{"done" "uploading"} status)
                         #js []
                         #js [($ GridActionsCellItem
                                 (if (= "error" status)
                                   {:icon ($ Icon "replay")
                                    :onClick (fn [e] (on-retry e o))
                                    :label "Retry"}
                                   {:icon ($ Icon "clear")
                                    :onClick (fn [e] (on-delete e o))
                                    :data-testid "deleteButton"
                                    :label "Delete"}))])))})

(defui upload-actions [{:keys [queue on-cancel on-start-upload]}]
  (let [active-upload (boolean (queue-first-by-status queue "uploading"))
        done (boolean (queue-first-by-status queue "done"))
        some-queued (some (fn [[_id item]]
                            (= "queue" (:status item)))
                          queue)]
    ($ DialogActions
       ;; 1. display disabled upload button until first uploads are done
       ;; 2. display upload button if we have a active upload
       ;; 3. display upload button if we have some queued items still
       ;;    - this can be true even if 1 and 2 aren't, when new items are
       ;;      added after the first items are uploaded.
       (if (and done
                (not active-upload)
                (not some-queued))
         ;; Once upload is done, just show one button "Done"
         ($ Button
            {:variant "contained"
             :color "primary"
             :data-testid "doneButton"
             :onClick on-cancel}
            "Done")
         ($ :<>
            ($ Button
               {:color "primary"
                :disabled active-upload
                :data-testid "cancelButton"
                :onClick on-cancel}
               "Cancel")
            ($ Button
               {:variant "contained"
                :color "primary"
                :disabled (or (not (seq queue)) (not some-queued))
                :data-testid "uploadButton"
                :onClick on-start-upload}
               "Upload"))))))

(defui upload-area-placeholder-view [{:keys [^js dropzone asset-type]
                                      :as props}]
  ($ :<>
     ($ dropzone-container
        {:dropzone dropzone
         :queue nil
         :asset-type asset-type}
        ($ upload-area-placeholder
           {:drag-active (.-isDragActive dropzone)
            :asset-type asset-type
            :modal-view true}))
     ($ upload-actions props)))

(def cell-test-id-mapping
  {"name" #js {:data-testid "nameColumn"}
   "type" #js {:data-testid "typeColumn"}
   "dimensions" #js {:data-testid "dimensionsColumn"}
   "status" #js {:data-testid "statusColumn"}})

(defn CellSlot [^js props]
  (let [update-props (get cell-test-id-mapping (-> props .-column .-field) #js {})
        new-props (js/Object.assign #js {} update-props props)]
    (react/createElement GridCell new-props)))

(defn RowSlot [^js props]
  (let [api (-> (useGridApiContext) .-current)
        row-id (-> props .-rowId)
        row (-> api (.getRow row-id))
        filename (:filename row)
        new-props (js/Object.assign #js {:data-testid filename} props)]
    (react/createElement GridRow new-props)))

(defn get-row-id [row]
  (:id row))

(defui upload-files-list
  [{:keys [queue dropzone asset-type columns header-height]
    :as props}]
  ($ :<>
     ($ dropzone-container
        {:dropzone dropzone
         :queue queue
         :asset-type asset-type}
        ($ DataGridPremium
           {:columns columns
            :data-testid "table"
            :getRowId get-row-id
            :onCellClick (fn [_ e _]
                           ;; Don't trigger droparea click when clicking on grid contents
                           (.stopPropagation e))
            ;; Allow status message to grow the row height
            :getRowHeight (fn [] "auto")
            :columnHeaderHeight header-height
            :disableRowSelectionOnClick true
            :disableColumnResize true
            :disableColumnReorder true
            :disableColumnFilter true
            :disableColumnMenu true
            :hideFooter true
            :rows (to-array (vals queue))
            :slots #js {:cell CellSlot
                        :row RowSlot
                        ;; Hides the separators between headers - TO BE TESTED
                        :columnResizeIcon (fn [] nil)}
            :sx #js {:borderBottom "0"
                     :borderTop "0"}}))
     ($ upload-actions props)))

(defui path-breadcrumbs [{:keys [header-height parent-nodes]}]
  ($ Grid
     ($ Breadcrumbs
        {:aria-label "breadcrumb"
         :variant "h6"
         :data-testid "modalTitle"
         :sx #js {:height header-height
                  :lineHeight (str header-height "px")
                  :borderBottom 1
                  :borderColor "grey.60"
                  :boxSizing "content-box"
                  :px 2}}
        (for [item parent-nodes]
          ($ Typography
             {:key (:asset-tree/id item)
              :variant "h6"
              :sx #js {:color "text.primary"
                       :lineHeight (str header-height "px")}}
             (:asset-tree/name item))))))

(defui sidebar
  "Placeholder for upload modal sidebar, contains just a header."
  [{:keys [measure-header-ref]}]
  ($ Grid
     {:size #js {:xs 4}
      :container true
      :direction "column"
      :sx #js {:bgcolor "grey.70"}}
     ($ Grid
        {:sx #js {:borderBottom 1
                  :borderColor "grey.60"}
         ;; Measure the element with border to use as data-grid header height also.
         :ref measure-header-ref}
        ($ DialogTitle
           {:variant "h2"
            :alignItems "center"
            :display "flex"
            :color "primary"}
           ($ Icon {:sx #js {:mr 1}} "cloud_upload")
           "Upload"))))

(defui modal-dialog [{:keys [header-height modal-open measure-header-ref on-close parent-nodes children]}]
  ($ Dialog
     {:open modal-open
      :onClose on-close
      :fullWidth true
      :maxWidth "lg"
      ;; Set content min-height
      :PaperProps #js {:sx #js {:minHeight "480px"}
                       :data-testid "modal"}}

     ($ Grid
        {:flex 1
         :container true}
        ($ sidebar
           {:measure-header-ref measure-header-ref})
        ($ Grid
           {:size #js {:xs 8}
            :container true
            :direction "column"}

           ($ path-breadcrumbs
              {:header-height header-height
               :parent-nodes  parent-nodes})

           children))))
